'use client'

import _ from 'lodash'
import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { useMemo, useState } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Calendar, ChevronLeft, ChevronRight } from 'react-feather'

import { useGetMeetingLinkQuery } from '@/core/graphql/hooks'
import { AvailabilityMeetingPlace } from '@/core/graphql/types'
import { generateSlots } from '@/utils/slots'

import { useBooking } from '../../model/bookingContext'
import { BookingStepEnum } from '../../model/bookingTypes'
import { useSchedules } from '../../model/useSchedules'
import { BookingStep } from './BookingStep'
import { IncompletedStepLabel } from './IncompletedStepLabel'
import { Summary } from './Summary'

export function AvailabilitiesStep() {
  const { goToStep, isStepCompleted, state } = useBooking()
  const isCompleted = isStepCompleted(BookingStepEnum.AVAILABILITIES)

  if (state.currentStep === BookingStepEnum.AVAILABILITIES) {
    return (
      <BookingStep className="sm:!rounded-br-none">
        <BookingStep.Title>
          <Trans>Meeting time</Trans>
        </BookingStep.Title>
        <BookingStep.Content>
          <Availabilities meetingLinkSlug={state.meetingLinkSlug} />
        </BookingStep.Content>
      </BookingStep>
    )
  }

  if (isCompleted) {
    return (
      <Summary
        icon="📅"
        label={t`Meeting time`}
        onEdit={() => goToStep(BookingStepEnum.AVAILABILITIES)}
        text={state.startDate ? dayjs(state.startDate).format('DD/MM/YYYY HH:mm') : ''}
      />
    )
  }

  return (
    <IncompletedStepLabel icon="📅">
      <Trans>Meeting time</Trans>
    </IncompletedStepLabel>
  )
}

type AvailabilitiesProps = {
  meetingLinkSlug: string
}

type DayAvailability = {
  date: string
  slots: string[]
}

export default function Availabilities({ meetingLinkSlug }: AvailabilitiesProps) {
  const { state, dispatch, goToNextStep } = useBooking()

  // Get values from context
  const meetingPlace = state.meetingPlace
  const meetingPlaceId = state.meetingPlaceId
  const speciality = state.selectedSpeciality

  const [currentCalendarViewStartDate, setCurrentCalendarViewStartDate] = useState<Dayjs | null>(dayjs())
  const [selectedSlot, setSelectedSlot] = useState<string>()

  const { data, loading } = useGetMeetingLinkQuery({ variables: { slug: meetingLinkSlug } })

  const meetingLink = data?.meetingLink

  const meetingLinkSchedules = useSchedules(meetingLink)

  const schedules = useMemo(
    () =>
      _.filter(
        meetingLinkSchedules,
        (schedule) =>
          _.includes(schedule.specialities, speciality) &&
          (schedule.meetingPlace === AvailabilityMeetingPlace.Center ? schedule.address?.placeId === meetingPlaceId : schedule.meetingPlace === meetingPlace)
      ),
    [meetingPlace, meetingPlaceId, meetingLinkSchedules, speciality]
  )

  const availabilities = useMemo(() => {
    if (!currentCalendarViewStartDate || !speciality) return []

    return _.range(7).map((dayOfTheWeek) => {
      const day = dayjs(currentCalendarViewStartDate.toISOString()).add(dayOfTheWeek, 'day')
      return {
        date: day.format('DD/MM/YYYY'),
        slots: _.uniqWith(
          _.flatten(
            schedules
              .filter((schedule) => schedule.specialities.includes(speciality))
              .map((schedule) => {
                if (schedule.isRecurrence) {
                  return generateSlots(
                    day.toDate(),
                    schedule.recurrence || [],
                    schedule.busySlots,
                    schedule.duration,
                    undefined,
                    schedule.unavailabilities
                  ).map((slot) => slot.start)
                }
                if (!schedule.scheduledDay || dayjs(schedule.scheduledDay).format('DD/MM/YYYY') !== day.format('DD/MM/YYYY')) return []

                return generateSlots(
                  day.toDate(),
                  (schedule.scheduledPeriods || []).map((period) => ({ days: [day.day()], ...period })),
                  schedule.busySlots,
                  schedule.duration,
                  undefined,
                  schedule.unavailabilities
                ).map((slot) => slot.start)
              })
          ),
          _.isEqual
        ),
      }
    })
  }, [currentCalendarViewStartDate, schedules, speciality])

  const handleSelectSlot = (startFrom: Date) => {
    const startDateIso = startFrom.toISOString()
    setSelectedSlot(startDateIso)

    // Store the selected date in context
    dispatch({
      type: 'SET_START_DATE',
      payload: startDateIso,
    })

    goToNextStep()
  }

  const moveCalendar = (days: number) => {
    setCurrentCalendarViewStartDate((current) => dayjs(current ?? undefined).add(days, 'day'))
  }

  // TODO: replace by AvailabilitiesSkeleton component
  if (loading) {
    return (
      <div className="flex min-h-40 items-center justify-center rounded-lg bg-white" role="status">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary" aria-hidden="true" />
        <span className="sr-only">
          <Trans>Loading...</Trans>
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5" data-testid="availability-picker">
      <div className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 p-2">
        <button
          aria-label={t`Previous week`}
          data-testid="previous-button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          onClick={() => moveCalendar(-7)}
          type="button"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 sm:text-base">
          <Calendar className="hidden h-4 w-4 text-primary sm:block" aria-hidden="true" />
          <span>
            {currentCalendarViewStartDate?.format('DD/MM/YYYY')} –{' '}
            {dayjs(currentCalendarViewStartDate ?? undefined)
              .add(6, 'day')
              .format('DD/MM/YYYY')}
          </span>
        </div>
        <button
          aria-label={t`Next week`}
          data-testid="next-button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          onClick={() => moveCalendar(7)}
          type="button"
        >
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div className="grid grid-cols-2 items-start gap-2.5 sm:grid-cols-7" data-testid="availability-count" data-days-count={availabilities.length}>
        {availabilities.map((availability) => (
          <DayColumn availability={availability} key={availability.date} onSelectSlot={handleSelectSlot} selectedSlot={selectedSlot} />
        ))}
      </div>
    </div>
  )
}

// Number of slots shown per day before the "Show more" toggle; keeps the week view compact.
const MAX_VISIBLE_SLOTS = 5

function DayColumn({
  availability,
  onSelectSlot,
  selectedSlot,
}: {
  availability: DayAvailability
  onSelectSlot: (startFrom: Date) => void
  selectedSlot?: string
}) {
  const [showAll, setShowAll] = useState(false)
  const [dayOfMonth, month, year] = availability.date.split('/')
  const day = dayjs(`${year}-${month}-${dayOfMonth}`)

  const visibleSlots = showAll ? availability.slots : availability.slots.slice(0, MAX_VISIBLE_SLOTS)
  const hiddenCount = availability.slots.length - visibleSlots.length
  const hasSlots = availability.slots.length > 0
  const isToday = day.isSame(dayjs(), 'day')

  return (
    <div className={clsx('flex flex-col overflow-hidden rounded-xl border', hasSlots ? 'border-gray-200 bg-white shadow-sm' : 'border-gray-100 bg-gray-50/50')}>
      <div className={clsx('px-2 py-2 text-center', isToday ? 'bg-primary/10' : 'bg-gray-50')}>
        <div className={clsx('text-[0.65rem] font-semibold uppercase tracking-wide', isToday ? 'text-primary' : 'text-gray-400')}>{day.format('ddd')}</div>
        <div className={clsx('text-sm font-bold', isToday ? 'text-primary' : 'text-gray-900')}>{day.format('DD/MM')}</div>
      </div>
      <div className="p-2">
        {hasSlots ? (
          <div className="flex flex-col gap-1.5">
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-1">
              {visibleSlots.map((slot) => {
                const isSelected = selectedSlot === new Date(slot).toISOString()
                return (
                  <button
                    key={slot}
                    type="button"
                    data-testid="appointment-slot"
                    className={clsx(
                      'w-full rounded-lg px-2 py-1.5 text-sm font-semibold tabular-nums transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
                      isSelected ? 'bg-primary text-white shadow-sm' : 'bg-primary/5 text-primary ring-1 ring-inset ring-primary/10 hover:bg-primary/10'
                    )}
                    onClick={() => onSelectSlot(new Date(slot))}
                  >
                    {dayjs(slot).format('HH:mm')}
                  </button>
                )
              })}
            </div>
            {availability.slots.length > MAX_VISIBLE_SLOTS && (
              <button
                type="button"
                data-testid="toggle-more-slots"
                className="w-full rounded-lg py-1 text-center text-xs font-semibold text-primary transition-colors hover:bg-primary/5"
                onClick={() => setShowAll((prev) => !prev)}
              >
                {showAll ? <Trans>Show less</Trans> : <Trans>Show {hiddenCount} more</Trans>}
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 px-1 py-3 text-center">
            <span className="h-0.5 w-5 rounded-full bg-gray-300" aria-hidden="true" />
            <span className="text-[0.65rem] leading-tight text-gray-400">
              <Trans>Not available currently</Trans>
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
